import { memo, useEffect, useRef, MutableRefObject } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import TWEEN, { Tween } from "three/examples/jsm/libs/tween.module.js";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { DeviceModel3DWrapper } from "./DeviceModel3D.styled";
import EventManager from '../../common/event-manager/EventManager';
import HtmlDomUtils from '../../../../../../util/HtmlDomUtils';
import StringUtils from '../../../../../../util/StringUtils';

interface AutoPlayPerspective {
  objectName: string;
  displayName?: string;
  object: THREE.Mesh | null;
  cameraPosition: THREE.Vector3;
  metricsNames?: string[];
  metricsKeys?: string[];
}

interface UIContext {
  autoPlaying: boolean;
  autoPlayingProspective: AutoPlayPerspective | null;
  autoPlayingProspectiveIndex: number;
  mouseMovePointer: { x: number; y: number } | null;
  mouseClickPointer: { x: number; y: number } | null;
  mouseClickFocusTweening: boolean;
  mouseClickIntersectedObjectsDepth: number;
  mouseClickSelectedObject: THREE.Mesh | null;
  originalObjectMaterialMap: Map<THREE.Object3D, THREE.Material | THREE.Material[]>;
  deviceView: any;
  latestDeviceMetrics: Record<string, { value: string | number | null }> | null;
  selectedDeviceMetrics: any;
  selectedDeviceMetricsProspective: AutoPlayPerspective | null;
}

interface ThreeContext {
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  labelRenderer: CSS2DRenderer;
}

interface DeviceModel3DProps {
  eventManager: EventManager
}

let DeviceModel3D: React.FC<DeviceModel3DProps> = memo((props) => {
  const uiContextRef = useRef<UIContext>({
    autoPlaying: true,
    autoPlayingProspective: null,
    autoPlayingProspectiveIndex: 0,
    mouseMovePointer: null,
    mouseClickPointer: null,
    mouseClickFocusTweening: false,
    mouseClickIntersectedObjectsDepth: 0,
    mouseClickSelectedObject: null,
    originalObjectMaterialMap: new Map(),
    deviceView: null,
    latestDeviceMetrics: null,
    selectedDeviceMetrics: null,
    selectedDeviceMetricsProspective: null,
  });

  const threeContextRef = useRef<ThreeContext | null>(null);
  const autoPlayIntervalRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const destroyScene = () => {
    if (autoPlayIntervalRef.current) {
      clearInterval(autoPlayIntervalRef.current);
      autoPlayIntervalRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (threeContextRef.current) {
      const ctx = threeContextRef.current;
      ctx.renderer.domElement.removeEventListener('dblclick', () => {});
      ctx.renderer?.dispose();
      ctx.labelRenderer.domElement.remove();
      ctx.renderer.domElement.remove();

      ctx.scene.traverse((obj) => {
        if ((obj as THREE.Mesh).geometry) {
          (obj as THREE.Mesh).geometry.dispose();
        }
        const mats = Array.isArray((obj as THREE.Mesh).material)
          ? (obj as THREE.Mesh).material
          : [(obj as THREE.Mesh).material];
        mats.forEach(mat => {
          mat?.dispose();
        });
      });
      threeContextRef.current = null;
    }
  };

  const initializeScene = async (dom: HTMLElement | null) => {
    if (!dom) {
      destroyScene();
      return;
    }
    const uiContext = uiContextRef.current;

    dom.addEventListener('mouseover', () => {
      uiContext.autoPlaying = false;
    });
    dom.addEventListener('mousemove', (e) => {
      uiContext.mouseMovePointer = {
        x: (e.offsetX - dom.clientWidth / 2) / (dom.clientWidth / 2),
        y: -(e.offsetY - dom.clientHeight / 2) / (dom.clientHeight / 2),
      };
    });
    dom.addEventListener('mouseout', () => {
      uiContext.autoPlaying = true;
    });
    dom.addEventListener('dblclick', (e) => {
      uiContext.mouseClickPointer = {
        x: (e.offsetX - dom.clientWidth / 2) / (dom.clientWidth / 2),
        y: -(e.offsetY - dom.clientHeight / 2) / (dom.clientHeight / 2),
      };
      uiContext.mouseClickFocusTweening = true;
      uiContext.selectedDeviceMetricsProspective = null;
      if (e.ctrlKey) uiContext.mouseClickIntersectedObjectsDepth++;
      else uiContext.mouseClickIntersectedObjectsDepth = 0;
    });
    dom.addEventListener('click', () => {
      const cam = threeContextRef.current?.camera;
      if (cam) console.log("Camera Pos:", cam.position);
    });

    const loader = new GLTFLoader();
    let tractorGltf: THREE.GLTF & { scene: THREE.Group };
    try {
      tractorGltf = await new Promise((resolve, reject) => {
        loader.load(
          '/static/3d-model/electric-wheel.glb',
          (gltf) => resolve(gltf),
          (progress) => {
            const percent = ((progress.loaded / progress.total) * 100).toFixed(1);
            console.log(`模型加载进度: ${percent}%`);
          },
          (err) => reject(err)
        );
      });
    } catch (err) {
      console.error("3D模型加载失败", err);
      return;
    }

    tractorGltf.scene.scale.set(200, 200, 200);
    tractorGltf.scene.position.set(0, 0, 0);
    tractorGltf.scene.rotation.set(0, 0, 0);

    const wireFrameMaterial = new THREE.MeshBasicMaterial({
      wireframe: true,
      transparent: true,
      opacity: 0.35,
      color: 0xE8870D,
    });
    wireFrameMaterial.depthWrite = false;

    const autoPlayingProspectives: AutoPlayPerspective[] = [
      {
        objectName: '行星减速机',
        object: null,
        cameraPosition: new THREE.Vector3(10.765962588695082, -7.230833094210927, 84.64408814080998),
        metricsNames: ['减速器温度'],
        metricsKeys: ['ch4'],
      },
      {
        objectName: '全盘湿式多片离合制动器',
        object: null,
        cameraPosition: new THREE.Vector3(90.91121051255405, 20.07660503794806, -136.07101047607014),
        metricsNames: ['离合器温度', '离合器压力'],
        metricsKeys: ['ch5', 'ch6'],
      },
      {
        objectName: '全盘湿式多片离合制动器-外端盖',
        object: null,
        cameraPosition: new THREE.Vector3(180.63165361364025, -11.777681058711904, 38.66830966799063),
      },
      {
        objectName: '行星减速机-齿圈',
        object: null,
        cameraPosition: new THREE.Vector3(-20.710227714308406, 37.870985472722886, 142.82323110289022),
      },
      {
        objectName: '全盘湿式多片驻车制动器',
        object: null,
        cameraPosition: new THREE.Vector3(-131.50885194916114, 28.435911454829384, 121.44836252697253),
      },
      {
        objectName: '全盘湿式多片驻车制动器002',
        displayName: '全盘湿式多片驻车制动器',
        object: null,
        cameraPosition: new THREE.Vector3(-131.50885194916114, 28.435911454829384, 121.44836252697253),
        metricsNames: ['制动器温度', '制动器压力'],
        metricsKeys: ['ch1', 'ch2'],
      },
      {
        objectName: '全盘湿式多片离合制动器-太阳轴-轴承',
        object: null,
        cameraPosition: new THREE.Vector3(61.68361344102594, 31.412780400740377, -149.43025236913633),
      },
      {
        objectName: '全盘湿式多片离合制动器-法兰盘',
        object: null,
        cameraPosition: new THREE.Vector3(98.13174235518808, 18.557658277509105, -72.32597417970264),
      },
      {
        objectName: '行星减速机-法兰盘',
        object: null,
        cameraPosition: new THREE.Vector3(-128.72366219595338, -7.481844287290733, 113.04720932535695),
      }
    ];

    const getObjectNameText = (objectName: string) => {
      return objectName.split(".")[0];
    };

    const getObjectLabelContent = (objectName: string) => {
      const matched = autoPlayingProspectives.find(p => p.objectName === objectName);
      const showName = matched?.displayName || getObjectNameText(objectName);

      const contentElement = document.createElement('div');
      contentElement.style.display = 'flex';
      contentElement.style.flexDirection = 'column';
      contentElement.style.gap = '6px';
      contentElement.style.color = '#fff';
      contentElement.style.fontSize = '13px';
      contentElement.style.fontFamily = 'system-ui, sans-serif';

      const nameRow = document.createElement('div');
      nameRow.style.display = 'flex';
      nameRow.style.justifyContent = 'space-between';
      nameRow.style.fontWeight = '600';
      nameRow.innerHTML = `<span>部件名称:</span><span>${showName}</span>`;
      contentElement.appendChild(nameRow);

      const target = autoPlayingProspectives.find(p => p.objectName === objectName);
      const metricsNames = target?.metricsNames ?? [];
      const metricsKeys = target?.metricsKeys ?? [];

      if (uiContext.latestDeviceMetrics && metricsKeys.length) {
        const metricsWrap = document.createElement('div');
        metricsWrap.style.display = 'flex';
        metricsWrap.style.flexDirection = 'column';
        metricsWrap.style.gap = '4px';

        for (let i = 0; i < metricsKeys.length; i++) {
          const mKey = metricsKeys[i];
          const mName = metricsNames[i];
          let val = uiContext.latestDeviceMetrics[mKey]?.value ?? null;

          if (val == null && uiContext.deviceView?.deviceTemplate?.configuration) {
            try {
              const cfg = JSON.parse(uiContext.deviceView.deviceTemplate.configuration);
              const props = cfg.properties || [];
              for (const prop of props) {
                if (prop.name === mName || prop.registerAddress === mKey) {
                  val = uiContext.latestDeviceMetrics[prop.key]?.value;
                  if (val != null) break;
                }
              }
            } catch (e) {}
          }

          const valStr = StringUtils.emptyToSlash(val as any);
          const row = document.createElement('div');
          row.style.display = 'flex';
          row.style.justifyContent = 'space-between';
          row.style.padding = '3px 0';
          row.innerHTML = `<span>${mName}</span><span style="color:#ffd266">${valStr}</span>`;
          metricsWrap.appendChild(row);
        }
        contentElement.appendChild(metricsWrap);
      }
      return contentElement;
    };

    tractorGltf.scene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        uiContext.originalObjectMaterialMap.set(mesh, mesh.material);

        if ((mesh.material as THREE.MeshStandardMaterial).metalness !== undefined) {
          const mat = mesh.material as THREE.MeshStandardMaterial;
          mat.metalness = Math.max(mat.metalness, 0.2);
          mat.roughness = Math.min(mat.roughness, 0.8);
          mat.needsUpdate = true;
        }

        const objName = getObjectNameText(mesh.name);
        const target = autoPlayingProspectives.find(p => p.objectName === objName);
        if (target) target.object = mesh;
      }
    });

    const scene = new THREE.Scene();
    scene.background = null;
    scene.add(tractorGltf.scene);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);

    const dirLightMain = new THREE.DirectionalLight(0xffffff, 1.3);
    dirLightMain.position.set(120, 120, 120);
    dirLightMain.castShadow = true;
    dirLightMain.shadow.mapSize.set(2048, 2048);
    dirLightMain.shadow.camera.near = 10;
    dirLightMain.shadow.camera.far = 600;
    dirLightMain.shadow.camera.left = -300;
    dirLightMain.shadow.camera.right = 300;
    dirLightMain.shadow.camera.top = 300;
    dirLightMain.shadow.camera.bottom = -300;
    scene.add(dirLightMain);

    const dirLightFill = new THREE.DirectionalLight(0xffffff, 0.75);
    dirLightFill.position.set(-100, -80, -100);
    scene.add(dirLightFill);

    const rimLight = new THREE.DirectionalLight(0xd0e8ff, 0.45);
    rimLight.position.set(-150, 80, -60);
    scene.add(rimLight);

    const camera = new THREE.PerspectiveCamera(
      60,
      dom.clientWidth / dom.clientHeight,
      0.5,
      2000
    );
    const cameraRadius = Math.sqrt(100 * 100 * 3);
    camera.position.set(164.67155358111378, 0.7680247223023541, 250.76421112114184);
    camera.lookAt(0, 0, 0);
    camera.layers.enableAll();

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
    });
    renderer.setClearAlpha(0);
    renderer.setSize(dom.clientWidth, dom.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true; 
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.6;
    dom.appendChild(renderer.domElement);

    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(dom.clientWidth, dom.clientHeight);
    labelRenderer.domElement.style.position = 'absolute';
    labelRenderer.domElement.style.top = '0';
    labelRenderer.domElement.style.left = '0';
    labelRenderer.domElement.style.pointerEvents = 'none';
    dom.appendChild(labelRenderer.domElement);

    const objectLabel = document.createElement('div');
    objectLabel.style.display = 'none';
    objectLabel.style.background = 'rgba(0,0,0,0.65)';
    objectLabel.style.padding = '10px 12px';
    objectLabel.style.border = '1px solid rgba(255, 200, 100, 0.6)';
    objectLabel.style.borderRadius = '6px';
    objectLabel.style.backdropFilter = 'blur(4px)';
    objectLabel.style.minWidth = '160px';
    labelRenderer.domElement.appendChild(objectLabel);

    const objectLabelCss = new CSS2DObject(objectLabel);
    objectLabelCss.position.set(0, 0, 0);
    objectLabelCss.center.set(0, 0);

    const raycaster = new THREE.Raycaster();
    raycaster.params.Line.threshold = 0.1;
    raycaster.params.Points.threshold = 0.1;

    const orbitControls = new OrbitControls(camera, renderer.domElement);
    orbitControls.enableDamping = true;
    orbitControls.dampingFactor = 0.06;
    orbitControls.minDistance = 80;
    orbitControls.maxDistance = 400;
    orbitControls.rotateSpeed = 0.8;
    orbitControls.zoomSpeed = 0.9;
    orbitControls.panSpeed = 0.8;
    orbitControls.update();
    threeContextRef.current = { scene, camera, renderer, controls: orbitControls, labelRenderer };

    const autoPlay = (targetPerspective: AutoPlayPerspective) => {
      if (!targetPerspective.object) return;
      const prev = uiContext.autoPlayingProspective;
      if (prev?.object) {
        prev.object.remove(objectLabelCss);
        prev.object.material = uiContext.originalObjectMaterialMap.get(prev.object)!;
      }
      uiContext.autoPlayingProspective = targetPerspective;
      targetPerspective.object.material = wireFrameMaterial;
      targetPerspective.object.add(objectLabelCss);
      HtmlDomUtils.removeChildren(objectLabel);
      objectLabel.appendChild(getObjectLabelContent(targetPerspective.object.name));
      objectLabel.style.display = 'block';

      const tween = new Tween(camera.position)
        .to(targetPerspective.cameraPosition, 1200)
        .easing(TWEEN.Easing.Cubic.InOut)
        .start();
    };

    const autoPlayTask = () => {
      if (!threeContextRef.current) {
        if (autoPlayIntervalRef.current) clearInterval(autoPlayIntervalRef.current);
        return;
      }
      if (uiContext.selectedDeviceMetricsProspective) return;
      if (uiContext.autoPlaying) {
        uiContext.autoPlayingProspectiveIndex = (uiContext.autoPlayingProspectiveIndex + 1) % autoPlayingProspectives.length;
        autoPlay(autoPlayingProspectives[uiContext.autoPlayingProspectiveIndex]);
      }
    };
    autoPlayIntervalRef.current = window.setInterval(autoPlayTask, 4000);
    autoPlayTask();

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      const ctx = threeContextRef.current;
      if (!ctx) return;

      if (uiContext.mouseClickFocusTweening && uiContext.mouseClickPointer) {
        uiContext.mouseClickFocusTweening = false;
        const mouseVec = new THREE.Vector2(
          uiContext.mouseClickPointer.x,
          uiContext.mouseClickPointer.y
        );
        raycaster.setFromCamera(mouseVec, ctx.camera);
        const meshes: THREE.Mesh[] = [];
        ctx.scene.traverse(c => {
          if ((c as THREE.Mesh).isMesh) meshes.push(c as THREE.Mesh);
        });
        const intersects = raycaster.intersectObjects(meshes, true);
        if (intersects.length > 0) {
          const pickIdx = uiContext.mouseClickIntersectedObjectsDepth % intersects.length;
          const selectedInter = intersects[pickIdx];
          const pickMesh = selectedInter.object as THREE.Mesh;

          const prevAuto = uiContext.autoPlayingProspective;
          if (prevAuto?.object) {
            prevAuto.object.remove(objectLabelCss);
            prevAuto.object.material = uiContext.originalObjectMaterialMap.get(prevAuto.object)!;
          }
          if (uiContext.mouseClickSelectedObject) {
            uiContext.mouseClickSelectedObject.remove(objectLabelCss);
            uiContext.mouseClickSelectedObject.material = uiContext.originalObjectMaterialMap.get(uiContext.mouseClickSelectedObject)!;
          }
          uiContext.mouseClickSelectedObject = pickMesh;
          pickMesh.material = wireFrameMaterial;
          pickMesh.add(objectLabelCss);
          HtmlDomUtils.removeChildren(objectLabel);
          objectLabel.appendChild(getObjectLabelContent(pickMesh.name));
          objectLabel.style.display = 'block';

          const targetPos = selectedInter.point.clone().multiplyScalar(2);
          new Tween(ctx.camera.position)
            .to(targetPos, 1200)
            .easing(TWEEN.Easing.Cubic.InOut)
            .start();
        }
      }

      TWEEN.update();
      ctx.controls.update();
      ctx.renderer.render(ctx.scene, ctx.camera);
      ctx.labelRenderer.render(ctx.scene, ctx.camera);
    };
    animate();

    props.eventManager.addDeviceViewUpdatedListener((deviceView) => {
      uiContext.deviceView = deviceView;
    });
    props.eventManager.addLatestDeviceMetricsUpdatedListener((latestMetrics) => {
      uiContext.latestDeviceMetrics = latestMetrics;
    });
    props.eventManager.addDeviceMetricSelectedListener((property, deviceMetric) => {
      uiContext.selectedDeviceMetrics = deviceMetric;
      const match = autoPlayingProspectives.find(p =>
        p.metricsNames?.includes(property.name)
      );
      if (match) {
        uiContext.selectedDeviceMetricsProspective = match;
        autoPlay(match);
      }
    });
  };

  useEffect(() => {
    return () => {
      destroyScene();
    };
  }, []);

  return <DeviceModel3DWrapper ref={initializeScene} />;
});

export default DeviceModel3D;
